import { AccountState } from "@generated/types";

/**
 * Access tier for the currently selected customer account.
 *
 * - "full"     — OPEN: everything available.
 * - "viewOnly" — SUSPENDED: informational surfaces only; all write/action
 *                flows are removed and write routes redirect to the dashboard.
 * - "blocked"  — OPENING / ACCEPTED / CLOSED / REJECTED / any unrecognised
 *                state: full-screen blocking page (AccountStateGate).
 * - "none"     — no account selected yet: the gate passes through.
 *
 * Lifecycle (upstream `core` CustomerAccount.accountState):
 *   OPENING → ACCEPTED → OPEN → SUSPENDED / CLOSED, or OPENING → REJECTED.
 * ACCEPTED means compliance passed but operations has not provisioned the
 * account yet — there is no ledger, no products and no recipients, so
 * view-only mode would only render empty screens. It stays hard-blocked with
 * its own "almost there" copy.
 */
export type AccountAccess = "full" | "viewOnly" | "blocked" | "none";

export function resolveAccountAccess(
  accountState: string | undefined
): AccountAccess {
  if (!accountState) return "none";
  switch (accountState) {
    case AccountState.Open:
      return "full";
    case AccountState.Suspended:
      return "viewOnly";
    case AccountState.Opening:
    case AccountState.Accepted:
    case AccountState.Closed:
    case AccountState.Rejected:
      return "blocked";
    default:
      // Unknown future states hard-block too (fail closed). They additionally
      // report telemetry in AccountStateGate — CUSTOMER-UI-79 was exactly
      // this: ACCEPTED/REJECTED existed upstream but not in the BFF contract.
      return "blocked";
  }
}

const LOCALE_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;

/**
 * True when a pathname belongs to a write/action flow that a SUSPENDED
 * (view-only) account must not reach:
 *
 * - /exchange and everything below it
 * - /pay/<anything> — amount entry, review, success. NOT /pay itself: that
 *   page is the recipients LIST, which view-only accounts may browse.
 * - /recipient/new and everything below it (create flow).
 *   /recipient/<type>/<id> (details view) stays reachable; its write
 *   affordances are hidden component-side.
 *
 * Handles optional locale prefixes ("/en-GB/pay") the same way as
 * shouldPollOnPage (next-intl localePrefix: "as-needed").
 */
export function isWriteRestrictedPath(pathname: string): boolean {
  if (!pathname) return false;
  const segments = pathname.split("/");
  const firstSegment = segments[1] ?? "";
  const routeSegments = (
    LOCALE_PATTERN.test(firstSegment) ? segments.slice(2) : segments.slice(1)
  ).filter(Boolean);

  const [head, second] = routeSegments;

  if (head === "exchange") return true;
  if (head === "pay") return routeSegments.length > 1;
  if (head === "recipient") return second === "new";

  return false;
}
